import {useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';

import type {ContentPart} from '../store';
import type {ComposerFile, FailedComposerImage} from './file';
import type {SelectedPanel} from './panel';
import type {SelectedRun} from './run';
import {draftKey, useDraftStore} from './store';

export interface DraftHandle {
  body: ContentPart[];
  panels: SelectedPanel[];
  runs: SelectedRun[];
  files: ComposerFile[];
  failedFiles: FailedComposerImage[];
  nonce: number;
  setBody: (body: ContentPart[]) => void;
  addPanel: (panel: SelectedPanel) => void;
  addRun: (run: SelectedRun) => void;
  removePanel: (panelIdentity: string) => void;
  removeRun: (runIdentity: string) => void;
  addFiles: (
    files: ComposerFile[],
    guard?: (next: ComposerFile[]) => boolean
  ) => boolean;
  removeFile: (id: string) => void;
  addFailedFiles: (failed: FailedComposerImage[]) => void;
  removeFailedFile: (id: string) => void;
  reset: () => void;
  setParts: (parts: ContentPart[]) => boolean;
}

/**
 * useDraft binds the composer-draft store to one conversation. Pass the active
 * thread's id, or null for the new-chat view; the returned actions target that
 * draft so callers never handle storage keys.
 */
export function useDraft(threadId: string | null): DraftHandle {
  const key = draftKey(threadId);
  const draft = useDraftStore(useShallow(s => s.drafts[key]));
  const actions = useDraftStore(
    useShallow(s => ({
      setBody: s.setBody,
      addPanel: s.addPanel,
      addRun: s.addRun,
      removePanel: s.removePanel,
      removeRun: s.removeRun,
      addFiles: s.addFiles,
      removeFile: s.removeFile,
      addFailedFiles: s.addFailedFiles,
      removeFailedFile: s.removeFailedFile,
      reset: s.reset,
      setDraftParts: s.setDraftParts,
    }))
  );

  return useMemo(
    () => ({
      body: draft?.body ?? [],
      panels: draft?.panels ?? [],
      runs: draft?.runs ?? [],
      files: draft?.files ?? [],
      failedFiles: draft?.failedFiles ?? [],
      nonce: draft?.nonce ?? 0,
      setBody: body => actions.setBody(key, body),
      addPanel: panel => actions.addPanel(key, panel),
      addRun: run => actions.addRun(key, run),
      removePanel: panelIdentity => actions.removePanel(key, panelIdentity),
      removeRun: runIdentity => actions.removeRun(key, runIdentity),
      addFiles: (files, guard) => actions.addFiles(key, files, guard),
      removeFile: id => actions.removeFile(key, id),
      addFailedFiles: failed => actions.addFailedFiles(key, failed),
      removeFailedFile: id => actions.removeFailedFile(key, id),
      reset: () => actions.reset(key),
      setParts: parts => actions.setDraftParts(key, parts),
    }),
    [key, draft, actions]
  );
}
